import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";

import * as yup from "yup";

import { requestPasswordChange } from "services/auth";
import { TextField } from "ui/components/TextField/TextField";
import { Loader } from "ui/components/Loader";

import { ForgotPasswordFormProps } from "./types";
import { validateYup } from "services/forms/validation/validation";
import { Errors } from "services/forms/validation/types";
import { Button } from "ui/components/Button/Button";
import { ForgotPasswordIntro } from "../ForgotPasswordIntro";
import { LoginPage } from "../../../LoginPage";

const IGNORE_ERROR = "IGNORE";
const yupSchema = yup.object().shape({
  email: yup
    .string()
    .typeError(IGNORE_ERROR)
    .email("Please provide a valid email"),
});

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = () => {
  const [email, setEmail] = useState<string | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleEmailChange = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = ev.target.value ? ev.target.value : null;
      setEmail(value);
    },
    []
  );

  const handleFormSubmit = useCallback(
    async (ev: React.FormEvent<HTMLFormElement>) => {
      ev.preventDefault();

      const isValid = validateYup({ email }, yupSchema, setErrors);
      if (!isValid) {
        return;
      }

      setIsLoading(true);
      try {
        await requestPasswordChange(email!);
      } catch {
        // Ignore error
      }
      setIsLoading(false);
    },
    [email]
  );

  return (
    <>
      <div className="flex h-screen w-screen">
        <ForgotPasswordIntro />
        <div className="w-full lg:w-1/2 flex justify-center items-center h-full">
          <div className="w-[400px]">
            <div className="text-center mb-4 text-[#344054]">
              <h1 className="text-2xl mb-1">Zaboravili ste lozinku?</h1>
              <p className="text-base">
                Unesite svoj email kako bi Vam mogli poslati email za
                resetiranje lozinke
              </p>
            </div>
            <form onSubmit={handleFormSubmit} noValidate>
              <div className="mb-4">
                <label className="block text-sm mb-2 font-medium leading-6 text-[#344054]">
                  Email
                </label>
                <TextField
                  type="email"
                  name="email"
                  placeholder="Unesite email adresu"
                  onChange={handleEmailChange}
                  error={errors.email}
                  value={email}
                  height="big"
                  required
                  disableDebounce
                />
              </div>
              <div className="mb-4">
                <Button
                  type="submit"
                  variant="primary"
                  size="full"
                  className="font-medium py-4 !text-base rounded-md"
                >
                  Pošalji
                </Button>
              </div>
              <p className="text-md text-center">
                Sjetili ste se lozinke?{" "}
                <Link to={LoginPage.route} className="text-blue-500">
                  Prijavite se
                </Link>
              </p>
            </form>
          </div>
        </div>
      </div>
      <Loader isLoading={isLoading ?? false}> </Loader>
    </>
  );
};

export default React.memo(ForgotPasswordForm);
